<template>
  <div class="card login-card">
    <p class="eyebrow">Workshop</p>
    <h1>The Unwritten 80%</h1>
    <p v-if="thanks" class="thanks">
      Thanks for telling us your college name. We will reach out.
    </p>
    <p class="muted lead">
      An imaginary journey from feature idea to production — clarity, design, CI/CD, and ownership.
    </p>

    <template v-if="isMock">
      <p class="mock-label">Mock mode — pick a role to demo</p>
      <div class="mock-actions">
        <button class="btn btn-primary" @click="enterAs('student')">
          Enter as Student (approved)
        </button>
        <button class="btn btn-secondary" @click="enterAs('pending')">
          Enter as Pending
        </button>
        <button class="btn btn-accent" @click="enterAs('admin')">
          Enter as Admin
        </button>
      </div>
      <p class="muted tip">
        Or set <code>localStorage.unwritten80_mock_role</code> to
        <code>student</code> / <code>admin</code> / <code>pending</code> / <code>removed</code>.
      </p>
    </template>

    <template v-else>
      <button class="btn btn-primary google" :disabled="loading" @click="onGoogle">
        Sign in with Google
      </button>
    </template>

    <p v-if="error" class="error">{{ error }}</p>
  </div>
</template>

<script setup>
import { ref } from 'vue'
import { useRouter } from 'vue-router'
import { isMockMode, mockSignIn, signInWithGoogle } from '../services/auth'

const THANKS_KEY = 'u80_request_thanks'

const router = useRouter()
const isMock = isMockMode()
const loading = ref(false)
const error = ref('')
let thanksOnce = false
try {
  thanksOnce = sessionStorage.getItem(THANKS_KEY) === '1'
  sessionStorage.removeItem(THANKS_KEY)
} catch {
  thanksOnce = false
}
const thanks = ref(thanksOnce)

async function enterAs(role) {
  error.value = ''
  loading.value = true
  try {
    try {
      sessionStorage.removeItem('u80_just_selected_college')
      sessionStorage.removeItem('u80_request_thanks')
      thanks.value = false
    } catch {
      /* ignore */
    }
    await mockSignIn(role)
    router.push('/')
  } catch (e) {
    error.value = e.message || 'Mock sign-in failed'
  } finally {
    loading.value = false
  }
}

async function onGoogle() {
  error.value = ''
  loading.value = true
  try {
    await signInWithGoogle()
  } catch (e) {
    error.value = e.message || 'Sign-in failed'
    loading.value = false
  }
}
</script>

<style scoped>
.login-card {
  text-align: center;
}

.eyebrow {
  text-transform: uppercase;
  letter-spacing: 0.12em;
  font-size: 0.75rem;
  font-weight: 600;
  color: var(--teal);
  margin: 0 0 0.5rem;
}

h1 {
  margin: 0 0 0.75rem;
  font-size: 2rem;
}

.lead {
  margin: 0 0 1.75rem;
}

.thanks {
  margin: 0 0 1rem;
  padding: 0.75rem 0.85rem;
  background: var(--paper-warm);
  border: 1px solid var(--border);
  border-radius: 6px;
  font-weight: 600;
}

.mock-label {
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.mock-actions {
  display: flex;
  flex-direction: column;
  gap: 0.6rem;
}

.tip {
  margin-top: 1.25rem;
  font-size: 0.85rem;
  text-align: left;
}

.tip code {
  font-size: 0.8rem;
  background: var(--paper-warm);
  padding: 0.1rem 0.35rem;
  border-radius: 3px;
}

.google {
  width: 100%;
}

.error {
  color: var(--danger);
  margin-top: 1rem;
}
</style>
